<template>
  <DataTableV01
    v-bind="$attrs"
    :headers="tableHeaders"
    :items="tableItems"
    :must-sort="true"
    :sort-by="initialSortKey"
    :sort-desc="initialSortDesc"
    @update:sort-by="emitSortKey"
    @update:sort-desc="emitSortOrder"
  >
    <template v-slot:header>
      <tr v-if="summaryRow" class="summary-row">
        <td
          v-for="column in columnValues"
          :key="'summary-'+column"
          align="left"
          v-html="getSummaryRowValue(column, summaryRow[column])"
        />
      </tr>
    </template>
    <template v-slot:[`item.experience_name_w_link`]="{ item }">
      <ExperienceLink
        :experienceJsonString="item.experience_name_w_link"
      />
    </template>
    <template v-slot:[`item.site_location_w_link`]="{ item }">
      <ExperienceLink
        :experienceJsonString="item.site_location_w_link"
      />
    </template>
  </DataTableV01>
</template>

<script>
import { mapState } from 'vuex';
import {
  getReportFieldFromSnake,
  getReportFieldKeyFromSnake,
} from '../../../services/dashboard/reportFieldService';
import {
  getDisplayValue,
  getSummaryDescriptor,
} from '../../../services/dashboard/displayFormatService';
import { getCurrencyFromString } from '../../../services/dashboard/dashboardUtils';
import ExperienceLink from './ExperienceLink.vue';
import { DataTableV01 } from '@mikmak/components';

export default {
  components: {
    ExperienceLink,
    DataTableV01,
  },
  props: {
    data: {
      type: Array,
    },
    summaryRow: {
      type: Object,
    },
    customColumns: {
      type: Array,
    },
    initialSortKey: String,
    initialSortDesc: Boolean,
    columnAlignment: {
      type: String,
      default: 'center',
      validator: v => {
        return ['center', 'left', 'right'].includes(v);
      },
    },
    truncateTextType: {
      type: String,
      validator: v => {
        return ['long', 'short'].includes(v);
      },
    },
  },
  computed: {
    ...mapState({
      allReportFields: state => state.dashboard.availableReportFields.allReportFields,
    }),
    columnValues() {
      if (this.customColumns) return this.customColumns;

      return this.data && this.data.length ? Object.keys(this.data[0]) : [];
    },
    tableHeaders() {
      return this.columnValues.map((col, i) => {

        const align = i === 0 ? 'left' : col.align || this.columnAlignment;
        const value = col.value || col;
        const text = col.name || this.getColumnName(col);
        const cellClass = i === 0 ? this.truncateTextClass : '';

        const headerObj = { align, value, text, cellClass };

        const reportField = getReportFieldFromSnake(this.allReportFields, value);

        headerObj.sort = this.getCustomSort(reportField);

        return headerObj;
      });
    },
    tableItems() {
      return this.data.map((row) => {
        const formattedRow = { ...row };

        for (const key in formattedRow) {
          formattedRow[key] = this.getCellValue(key, formattedRow[key]);
        }

        return formattedRow;
      });
    },
    truncateTextClass() {
      if (!this.truncateTextType) return '';

      return this.truncateTextType === 'short'
        ? 'truncate-text-short'
        : 'truncate-text-long';
    },
  },
  methods: {
    getColumnName(column) {
      return getReportFieldKeyFromSnake(this.allReportFields, column);
    },
    getCellValue(column, val) {
      const reportField = getReportFieldFromSnake(this.allReportFields, column);

      return getDisplayValue(reportField, val);
    },
    getSummaryRowValue(column, val) {
      const reportField = getReportFieldFromSnake(this.allReportFields, column);
      const cellValue = this.getCellValue(column, val);
      const descriptor = getSummaryDescriptor(reportField);

      return `<b>${cellValue}</b> ${descriptor}`;
    },
    getCustomSort(reportField = {}) {
      const {
        itemOrder: customOrder,
        sortType,
        type: fieldType,
        value: fieldValue,
      } = reportField;

      switch (true) {
        case (fieldValue === 'avgInteractionTime'):
        case (fieldValue === 'purchaseIntentValue'):
          return this.sortWithNA;
        case (sortType === 'currency'):
          return (a, b) => getCurrencyFromString(a) - getCurrencyFromString(b);

        case (sortType === 'custom'):
          return (a, b) => {
            return customOrder.indexOf(a.toLowerCase()) - customOrder.indexOf(b.toLowerCase());
          };
        case (fieldType === 'rate'):
          return (a, b) => parseFloat(a) - parseFloat(b);
        case (fieldType === 'sum'):
        case (fieldType === 'avg'):
          return (a, b) => a.replace(/(^\$|,)/g, '') - b.replace(/(^\$|,)/g, '');
        default:
          return undefined;
      }
    },
    sortWithNA(a, b) {
      // Some columns can contain 'NA' alongside actual numbers and $ amounts.
      // So 'NA' needs to be treated like $0 or 0 and custom sorted as the "lowest" value always.
      if (a === 'NA' && b !== 'NA') return -1;
      if (a !== 'NA' && b === 'NA') return 1;

      return a.replace(/(^\$|,)/g, '') - b.replace(/(^\$|,)/g, '');
    },
    emitSortKey(val) {
      this.$emit('updateSortKey', val);
    },
    emitSortOrder(val) {
      this.$emit('updateSortOrder', val ? 1 : -1);
    },
  },
};
</script>
<style lang="scss">
.summary-row {
  td {
    border-top: 1px solid #C4CBD3;
    border-bottom: 1px solid #C4CBD3;
    padding: 8px 16px;
  }
}

.truncate-text-short {
  overflow: hidden;
  text-overflow: ellipsis;
  height: 40px;
  max-width: 120px;
  white-space: nowrap;
}

.truncate-text-long {
  overflow: hidden;
  text-overflow: ellipsis;
  height: 40px;
  max-width: 300px;
  white-space: nowrap;
}
</style>
